import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { MatSnackBarRef } from '@angular/material/snack-bar';
import { timeout } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { SnackBarService } from './snackBar.service';
import { NotificationSnackComponent } from '../../components/notification-snack/notification-snack.component';

const PING_INTERVAL_MS  = 30_000;
const PING_TIMEOUT_MS   = 3_000;
const THRESHOLD_SLOW_MS = 2_000;  // > 2 s → muy lenta / error
const THRESHOLD_WARN_MS =   800;  // 800 ms – 2 s → inestable

@Injectable({ providedIn: 'root' })
export class NetworkService implements OnDestroy {

  private _pingInterval: any;
  private _offlineRef: MatSnackBarRef<NotificationSnackComponent> | null = null;
  private _isMonitoring = false;
  private _pendingPing = false;

  // Arrow functions para que `this` se conserve en addEventListener
  private readonly _onOnline  = () => this._handleOnline();
  private readonly _onOffline = () => this._handleOffline();

  constructor(
    private _snack: SnackBarService,
    private _http: HttpClient
  ) {}

  // ── Iniciar monitoreo (llamar desde AppComponent.ngOnInit) ───────────────
  startMonitoring(): void {
    if (this._isMonitoring) { return; }
    this._isMonitoring = true;

    window.addEventListener('online',  this._onOnline);
    window.addEventListener('offline', this._onOffline);

    if (!navigator.onLine) { this._handleOffline(); }
  }

  stopMonitoring(): void {
    if (!this._isMonitoring) { return; }
    this._isMonitoring = false;

    window.removeEventListener('online',  this._onOnline);
    window.removeEventListener('offline', this._onOffline);
    clearInterval(this._pingInterval);
    this._pingInterval = null;
    this._closeOfflineToast();
  }

  ngOnDestroy(): void {
    this.stopMonitoring();
  }

  // ── Evento: navegador dice "offline" ─────────────────────────────────────
  private _handleOffline(): void {
    if (this._offlineRef) { return; } // ya hay un toast offline activo

    this._offlineRef = this._snack.show(
      'error',
      'Revisa tu red e intenta nuevamente.',
      'Sin conexión a internet',
      0  // persistente
    );
  }

  // ── Evento: navegador dice "online" ──────────────────────────────────────
  private _handleOnline(): void {
    this._closeOfflineToast();

    this._snack.show(
      'success',
      'Tu conexión a internet ha vuelto.',
      'Conexión restablecida',
      3500
    );

    // Confirmar calidad real inmediatamente al reconectar
    this._checkQuality();
  }

  // ── Ping real al backend ─────────────────────────────────────────────────
  private _checkQuality(): void {
    if (this._pendingPing) { return; } // no lanzar otro si hay uno en vuelo

    const pingUrl = environment.api.url + 'api/health';
    const start = Date.now();
    this._pendingPing = true;

    this._http.get(pingUrl, { responseType: 'text' })
      .pipe(timeout(PING_TIMEOUT_MS))
      .subscribe({
        next: () => {
          this._pendingPing = false;
          const elapsed = Date.now() - start;

          if (elapsed >= THRESHOLD_SLOW_MS) {
            this._snack.show('error', 'Tu conexión es demasiado lenta. Verifica tu red.', 'Conexión muy lenta', 6000);
          } else if (elapsed >= THRESHOLD_WARN_MS) {
            this._snack.show('warning', 'Algunas acciones pueden tardar más de lo normal.', 'Conexión inestable', 5000);
          }
        },
        error: (err) => {
          this._pendingPing = false;

          if (err instanceof HttpErrorResponse && err.status === 404) { return; }

          this._snack.show('warning', 'Algunas acciones pueden tardar más de lo normal.', 'Conexión inestable', 5000);
        }
      });
  }

  // ── Auxiliar: cerrar el toast offline si sigue activo ───────────────────
  private _closeOfflineToast(): void {
    if (this._offlineRef) {
      this._snack.close(this._offlineRef);
      this._offlineRef = null;
    }
  }
}
